import * as React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { groupBy, orderBy } from 'lodash'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/StructuredPicker/index.styles.css'
import {
  PickerContainer,
  PickerOption,
} from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/StructuredPicker/shared'
import {
  CostCenterOption,
  useCostCenterByLegalEntities,
} from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'

interface JobStructureGroup {
  groupKey: string
  groupName: string
  options: CostCenterOption[]
}

interface Props {
  value?: { id: string; name: string }
  placeholder: string
  selectedIds: number[]
  legalEntityIds: number[]
  onChange: (option: CostCenterOption) => void
  readonly?: boolean
}

const JobStructurePicker = ({
  value,
  placeholder,
  selectedIds: _selectedIds,
  legalEntityIds,
  onChange,
  readonly,
}: Props) => {
  const [open, setOpen] = React.useState(false)
  const [expandedGroups, setExpandedGroups] = React.useState<string[]>([])

  const {
    jobStructureOptions: options,
    controls,
    loading,
    debouncedOnSearch,
    clearSearch,
  } = useCostCenterByLegalEntities({
    excludeCostCenterIds: [],
    legalEntityIds,
    onlyJobCatalog: true,
  })

  useUnsafeEffect(() => {
    if (!open) {
      setExpandedGroups([])
      clearSearch()
    }
  }, [open, clearSearch])

  const handleValueChange = (option: CostCenterOption) => {
    setOpen(false)
    onChange(option)
  }

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupKey) ? prev.filter((k) => k !== groupKey) : [...prev, groupKey]
    )
  }

  const needsGrouping = React.useMemo(() => {
    return options.some(
      (opt) => opt.jobCatalogFunctionName != null || opt.jobCatalogRoleName != null
    )
  }, [options])

  const groupedOptions: JobStructureGroup[] = React.useMemo(() => {
    if (!needsGrouping) {
      return []
    }

    const grouped = groupBy(options, (opt) => {
      const family = opt.jobCatalogFamilyName || ''
      const func = opt.jobCatalogFunctionName || ''
      return `${family}|${func}`
    })

    return orderBy(
      Object.entries(grouped).map(([key, opts]) => {
        const [family = '', func = ''] = key.split('|')
        const groupName = func ? `${family} | ${func}` : family
        return {
          groupKey: key,
          groupName,
          options: orderBy(opts, (o) => o.name, ['asc']),
        }
      }),
      (g) => g.groupName,
      ['asc']
    )
  }, [options, needsGrouping])

  const flatOptions = React.useMemo(() => {
    return orderBy(options, (o) => o.name, ['asc'])
  }, [options])

  const unselectedValue = !value || Number(value.id) < 0

  const isOptionSelected = (optionId: string) => {
    return value?.id === optionId
  }

  const renderGroupedContent = () => (
    <>
      {groupedOptions.map((group) => {
        const isExpanded = expandedGroups.includes(group.groupKey)
        const groupHasSelectedItem = group.options.some((opt) => opt.id === value?.id)

        return (
          <Box key={group.groupKey}>
            {/* Group Header */}
            <Box
              alignItems="center"
              justifyContent="spaceBetween"
              background="grey000White"
              cursor="pointer"
              flexDirection="row"
              gap="s8"
              paddingX="s16"
              paddingY="s12"
              width="full"
              onClick={() => toggleGroup(group.groupKey)}
              className={'StructuredPicker_optionHover'}
            >
              <Box flexDirection="row" alignItems="center" gap="s8" flexGrow>
                <Icon.Small icon={isExpanded ? 'chevron-down' : 'chevron-right'} color="grey600" />
                <Text.Normal size="200" weight="medium" ellipsis>
                  {group.groupName}
                </Text.Normal>
                <Text.Normal size="100" color="grey600">
                  {group.options.length}
                </Text.Normal>
              </Box>
              {groupHasSelectedItem && !isExpanded && (
                <Icon.Small icon="check" color="viridian600" />
              )}
            </Box>

            {/* Expanded Group Options */}
            {isExpanded && (
              <Box paddingLeft="s24">
                {group.options.map((option) => (
                  <PickerOption
                    key={option.id}
                    option={option}
                    isSelected={isOptionSelected(option.id)}
                    onSelect={handleValueChange}
                  />
                ))}
              </Box>
            )}
          </Box>
        )
      })}
    </>
  )

  const renderFlatContent = () => (
    <>
      {flatOptions.map((option) => (
        <PickerOption
          key={option.id}
          option={option}
          isSelected={isOptionSelected(option.id)}
          onSelect={handleValueChange}
        />
      ))}
    </>
  )

  return (
    <PickerContainer
      open={open}
      readonly={readonly}
      onOpenChange={() => setOpen(!open)}
      disabled={options.length < 1 || !!readonly}
      placeholder={placeholder}
      displayValue={unselectedValue ? undefined : value?.name}
      onSearchChange={debouncedOnSearch}
      loading={loading}
      controls={controls}
      hasOptions={options.length > 0}
    >
      {needsGrouping ? renderGroupedContent() : renderFlatContent()}
    </PickerContainer>
  )
}

export default JobStructurePicker
